import { useEffect, useState, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Loader2, Truck, Eye, Plus, Sparkles, ArrowUpDown, ArrowUp, ArrowDown, FileUp, Search, RefreshCw, Download, Filter } from 'lucide-react';
import { purchasingAPI, coreAPI, fetchAllPages, AdvancedShippingNotice, Site, CompanyGroup } from '../../../lib/api';
import { CreateASNModal, ASNDetailModal } from '../ASNModals';
import { ImportAllocateModal } from '../asn-wizard/ImportAllocateModal';
import { Pagination } from '../../ui/pagination';
import { SectionTourTrigger } from '../../tour/SectionTourTrigger';
import { useAuth } from '../../../contexts/AuthContext';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useView } from '../../../contexts/ViewContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { ASNFilterSection } from './ASNFilterSection';
import { toast } from 'sonner';
import { asnDisplayStatus } from '../asnDeliveryType';
import { useASNFilterCascades, ASNCascadeFilterOptions } from './asn/useASNFilterCascades';
import { pageCountFromResponse, type PageSizeMemory } from '../../../hooks/usePagination';

/** CR-6449: Initial/empty filter state shape for the two-state pattern */
const EMPTY_FILTERS = {
  asnNumber: '',
  partSearch: '',
  supplier: '',
  trackingNumber: '',
  poNumber: '',
  poLineNumber: '',
  partNumber: '',
  description: '',
  status: '',
  courier: '',
  courierDeliveryStatus: '',
  dateFrom: '',
  dateTo: '',
  site: '',
  warehouse: '',
  supplierCountry: '',
  supplierGroup: '',
  commodityGroup: '',
  commodityType: '',
  strategic: '',
  // CR-6441: New cascade filter fields
  division: '',
  companyCountry: '',
  // CR-6441: New fields per diagram
  supplierCode: '',
  buyerId: '',
};

// CR-3420: Sortable columns configuration
type SortDirection = 'asc' | 'desc' | null;
interface SortConfig {
  column: string;
  direction: SortDirection;
}

export default function ASNTab() {
  const { user } = useAuth();
  // CR-6160: ASN logistics tracking feature flag - gates courier status filter
  const { isFeatureEnabled } = useMenuSettings();
  const trackingEnabled = isFeatureEnabled('asn_logistics_tracking');
  // CR-6038: react to the top-bar view-context (company/group/site).
  // viewKey is a stable string that flips on every switch (drives refetch);
  // the level + ids let us send the scope as an explicit query param so the
  // request is self-describing and never races the context-persist PATCH.
  const { viewKey, viewLevel, selectedGroupId, selectedCompanyId, selectedSiteId } = useView();
  // ASN creation is supplier-only (suppliers create ASNs to notify customers of shipments)
  const isSupplier = user?.platform === 'SUPPLIER';
  const isHelixUser = user?.company_type === 'helix';

  const [asns, setAsns] = useState<AdvancedShippingNotice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const servedPageRef = useRef<PageSizeMemory | undefined>(undefined);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // CR-7142: how many wholly-direct loads have been acknowledged. Drives
  // whether the Acknowledged tab is offered at all — a tenant that never ships
  // direct should not carry a tab that can only ever come back empty.
  const [directAckCount, setDirectAckCount] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAsn, setSelectedAsn] = useState<AdvancedShippingNotice | null>(null);
  const [createInitialCallOffId, setCreateInitialCallOffId] = useState<number | null>(null);
  // CR-6038: standalone Import & Allocate workflow + prefilled-line state.
  const [showImportAllocate, setShowImportAllocate] = useState(false);
  const [createPrefilledDeliveryLineIds, setCreatePrefilledDeliveryLineIds] = useState<number[]>([]);
  const [pageSize, setPageSize] = useState(50);
  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-open Create ASN wizard when navigated via /purchasing?tab=asn&action=create&call_off=<id>
  useEffect(() => {
    if (!isSupplier) return;
    if (searchParams.get('action') !== 'create') return;
    const callOffParam = searchParams.get('call_off');
    setCreateInitialCallOffId(callOffParam ? Number(callOffParam) : null);
    setShowCreateModal(true);
    // Clear the trigger params so reloads/back-navigations don't reopen the modal.
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    next.delete('call_off');
    setSearchParams(next, { replace: true });
  }, [searchParams, isSupplier, setSearchParams]);

  // CR-6291: deep-link straight to one ASN's detail via /purchasing?tab=asn&asn=<id>
  // (e.g. the call-off acknowledge prompt's "View ASN" button) so it opens THAT
  // call-off's ASN rather than just landing on the full ASN list. A ref guard
  // ensures we handle each id once — clearing the param must NOT cancel the
  // in-flight fetch (an effect-cleanup cancel was leaving the user on the list).
  const handledAsnDeepLinkRef = useRef<string | null>(null);
  useEffect(() => {
    const asnParam = searchParams.get('asn');
    if (!asnParam || handledAsnDeepLinkRef.current === asnParam) return;
    handledAsnDeepLinkRef.current = asnParam;
    const asnId = Number(asnParam);
    // Clear the param so reloads / back-nav don't reopen it.
    const next = new URLSearchParams(searchParams);
    next.delete('asn');
    setSearchParams(next, { replace: true });
    if (!Number.isFinite(asnId)) return;
    purchasingAPI.getASN(asnId)
      .then((res) => {
        setSelectedAsn(res.data);
        setShowDetailModal(true);
      })
      .catch(() => { /* ASN not accessible — stay on the list */ });
  }, [searchParams, setSearchParams]);

  // CR-3420: Sort state
  const [sortConfig, setSortConfig] = useState<SortConfig>({ column: 'created_at', direction: 'desc' });

  /**
   * CR-6449: Two-state filter pattern (like ForecastTab) to prevent
   * re-rendering the data table on every filter input change.
   *
   * `filters` = draft state for the filter UI (updates immediately when user types/selects)
   * `appliedFilters` = applied state that triggers data fetching (only updates on "Apply Filters")
   *
   * The data table only re-renders when `appliedFilters` changes.
   */
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });

  // Text input display state (for responsive typing)
  const [textInputs, setTextInputs] = useState({
    asnNumber: '',
    supplier: '',
    trackingNumber: '',
    poNumber: '',
    poLineNumber: '',
    partNumber: '',
    description: '',
  });

  // CR-6449: Quick select date range state
  const [quickSelectValue, setQuickSelectValue] = useState('');

  // Filter dropdown options
  // CR-6449: Added warehouseOptions and supplierCountryOptions
  const [siteOptions, setSiteOptions] = useState<{ value: string; label: string }[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<{ value: string; label: string }[]>([]);
  const [supplierCountryOptions, setSupplierCountryOptions] = useState<{ value: string; label: string }[]>([]);
  const [supplierGroupOptions, setSupplierGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityGroupOptions, setCommodityGroupOptions] = useState<{ value: string; label: string }[]>([]);
  const [commodityTypeOptions, setCommodityTypeOptions] = useState<{ value: string; label: string }[]>([]);
  // CR-6441: New cascade filter state
  const [divisionOptions, setDivisionOptions] = useState<{ value: string; label: string }[]>([]);
  const [siteCountries, setSiteCountries] = useState<string[]>([]);
  const [cascadeFilterOptions, setCascadeFilterOptions] = useState<ASNCascadeFilterOptions | null>(null);

  // CR-6441: Build typed filters for cascade hook
  const typedFilters = useMemo(() => ({
    asnNumber: filters.asnNumber || '',
    partSearch: filters.partSearch || '',
    supplier: filters.supplier || '',
    trackingNumber: filters.trackingNumber || '',
    poNumber: filters.poNumber || '',
    poLineNumber: filters.poLineNumber || '',
    partNumber: filters.partNumber || '',
    description: filters.description || '',
    status: filters.status || '',
    courier: filters.courier || '',
    courierDeliveryStatus: filters.courierDeliveryStatus || '',
    dateFrom: filters.dateFrom || '',
    dateTo: filters.dateTo || '',
    site: filters.site || '',
    warehouse: filters.warehouse || '',
    supplierCountry: filters.supplierCountry || '',
    supplierGroup: filters.supplierGroup || '',
    commodityGroup: filters.commodityGroup || '',
    commodityType: filters.commodityType || '',
    strategic: filters.strategic || '',
    division: filters.division || '',
    companyCountry: filters.companyCountry || '',
    // CR-6441: New fields per diagram
    supplierCode: filters.supplierCode || '',
    buyerId: filters.buyerId || '',
  }), [filters]);

  // CR-6441: Internal filter change for cascade hook
  // Also clears textInputs for text-based fields (like supplier) so the UI reflects the change
  const handleCascadeFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    // If clearing a text input field, also clear the textInputs state so the UI updates
    if (value === '' && ['asnNumber', 'supplier', 'trackingNumber', 'poNumber', 'poLineNumber', 'partNumber', 'description'].includes(key)) {
      setTextInputs((prev) => ({ ...prev, [key]: value }));
    }
  };

  // CR-6441: Use cascade hook for dependent filter logic
  // Note: supplierSiteOptions is used internally by the hook for validation,
  // but ASN uses text input for supplier filtering, so we don't render it directly.
  const {
    siteOptions: cascadedSiteOptions,
    commodityTypeOptions: cascadedCommodityTypes,
    handleDivisionChange,
    handleCompanyCountryChange,
    handleSiteChange,
    handleSupplierCountryChange,
    handleSupplierGroupChange,
    handleCommodityGroupChange,
  } = useASNFilterCascades(typedFilters, cascadeFilterOptions, handleCascadeFilterChange);

  // CR-6441: Use cascaded options when available
  const effectiveSiteOptions = useMemo(() => {
    if (cascadeFilterOptions && cascadeFilterOptions.sites.length > 0) {
      return cascadedSiteOptions.map((s) => ({ value: String(s.id), label: s.name }));
    }
    return siteOptions;
  }, [cascadeFilterOptions, cascadedSiteOptions, siteOptions]);

  const effectiveCommodityTypeOptions = useMemo(() => {
    if (cascadeFilterOptions && cascadeFilterOptions.commodityTypes.length > 0) {
      return cascadedCommodityTypes.map((t) => ({ value: String(t.id), label: t.name }));
    }
    return commodityTypeOptions;
  }, [cascadeFilterOptions, cascadedCommodityTypes, commodityTypeOptions]);

  // CR-4580: No filter persistence — always start fresh
  // CR-6449: Added fetching of warehouse and supplier country options
  // CR-6441: Added fetching of divisions for cascade filtering via getLineFilterOptions
  useEffect(() => {
    setInitialLoadComplete(true);
    // Fetch filter options
    Promise.all([
      coreAPI.getAccessibleSites().catch(() => ({ data: [] })),
      coreAPI.getAccessibleWarehouses().catch(() => ({ data: [] })),
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      // The unfiltered list is Helix-only (403 otherwise): customers read the
      // scoped for-filter endpoint, suppliers have no supplier-group filter.
      isSupplier
        ? Promise.resolve([] as CompanyGroup[])
        : isHelixUser
          ? fetchAllPages<CompanyGroup>((page) => coreAPI.getCompanyGroups({ page_size: 100, page, company_type: 'supplier' })).catch(() => [] as CompanyGroup[])
          : coreAPI.getSupplierGroupsForFilter().then((res) => res.data.results || []).catch(() => [] as CompanyGroup[]),
      // CR-6449: Fetch supplier sites for country extraction
      coreAPI.getSupplierSitesForFilter().catch(() => ({ data: { results: [] } })),
      // CR-6441: Fetch divisions, commodity groups/types, and countries from line filter options
      // This is the same endpoint used by Forecast tab and returns proper commodity data
      purchasingAPI.getLineFilterOptions().catch(() => ({ data: {} })),
    ]).then(([sitesRes, warehousesRes, companyGroups, suppliersRes, lineFilterRes]) => {
      const sites = (Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data as unknown as { results: Site[] }).results || []) as Site[];
      setSiteOptions(sites.map((s) => ({ value: String(s.id), label: s.name })));

      // CR-6449: Set warehouse options
      const warehouses = Array.isArray(warehousesRes.data) ? warehousesRes.data : [];
      setWarehouseOptions(warehouses.map((w: { id: number; name: string }) => ({ value: String(w.id), label: w.name })));

      // CR-6449: Extract unique countries from suppliers
      const suppliersData = (suppliersRes.data as { results?: Array<{ id: number; name: string; country?: string; group?: number | null; customers?: number[] }> })?.results || [];
      const uniqueSupplierCountries = Array.from(new Set(suppliersData.map((s) => s.country).filter(Boolean))) as string[];
      setSupplierCountryOptions(uniqueSupplierCountries.sort().map((c) => ({ value: c, label: c })));

      // CR-6441: Extract data from line filter options (same endpoint as Forecast tab)
      const lineData = lineFilterRes.data as {
        divisions?: Array<{ id: number; name: string }>;
        site_countries?: string[];
        supplier_countries?: string[];
        suppliers?: Array<{ id: number; name: string; group?: number | null; country?: string | null; customers?: number[] }>;
        customers?: Array<{ id: number; name: string; group?: number | null; country?: string | null }>;
        commodity_groups?: Array<{ id: number; name: string }>;
        commodity_types?: Array<{ id: number; name: string; commodity_group_id: number }>;
      };

      // CR-6441: Use site countries from line filter options or extract from sites
      const siteCountriesFromApi = lineData.site_countries || [];
      const uniqueSiteCountries = siteCountriesFromApi.length > 0
        ? siteCountriesFromApi
        : Array.from(new Set(sites.map((s) => (s as Site & { country?: string }).country).filter(Boolean))) as string[];
      setSiteCountries(uniqueSiteCountries.sort());

      // CR-6441: Use supplier data from line filter options if available (has cascade fields)
      const lineSuppliers = lineData.suppliers || [];
      const allSuppliers = lineSuppliers.length > 0 ? lineSuppliers : suppliersData;

      // CR-6441: Use customers from line filter options for cascade sites (has proper group field)
      const lineSites = lineData.customers || [];

      // CR-6441: Use commodity groups/types from lineFilterOptions (same as Forecast tab)
      const groups = lineData.commodity_groups || [];
      setCommodityGroupOptions(groups.map((g) => ({ value: String(g.id), label: g.name })));

      const types = lineData.commodity_types || [];
      setCommodityTypeOptions(types.map((t) => ({ value: String(t.id), label: t.name })));

      setSupplierGroupOptions(companyGroups.map((g: CompanyGroup) => ({ value: String(g.id), label: g.name })));

      // CR-6441: Set division options from line filter options
      const divisions = lineData.divisions || [];
      setDivisionOptions(divisions.map((d) => ({ value: String(d.id), label: d.name })));

      // CR-6441: Build cascade filter options
      // Use customers from lineFilterOptions (has proper group field) if available,
      // otherwise fall back to sites with group_id mapping
      const cascadeSites = lineSites.length > 0
        ? lineSites.map((c) => ({
            id: c.id,
            name: c.name,
            group: c.group ?? null,
            country: c.country ?? null,
          }))
        : sites.map((s) => ({
            id: s.id,
            name: s.name,
            group: s.group_id ?? null,  // Fall back to group_id from Site type
            country: s.country ?? null,
          }));

      setCascadeFilterOptions({
        suppliers: allSuppliers.map((s) => ({
          id: s.id,
          name: s.name,
          group: s.group ?? null,
          customers: s.customers ?? [],
          country: s.country ?? null,
        })),
        commodityGroups: groups.map((g) => ({ id: g.id, name: g.name })),
        // Note: lineFilterOptions uses commodity_group_id instead of commodity_group
        commodityTypes: types.map((t) => ({
          id: t.id,
          name: t.name,
          commodity_group: t.commodity_group_id ?? 0,
        })),
        supplierGroups: companyGroups.map((g: CompanyGroup) => ({ id: g.id, name: g.name })),
        sites: cascadeSites,
        divisions: divisions.map((d) => ({ id: d.id, name: d.name })),
        siteCountries: uniqueSiteCountries,
        supplierCountries: uniqueSupplierCountries,
      });
    });
  }, [isSupplier, isHelixUser]);

  /**
   * CR-6449: Data fetching depends on `appliedFilters` (not `filters`).
   * This ensures the data table only re-renders when "Apply Filters" is clicked,
   * not on every keystroke in the filter inputs.
   */
  useEffect(() => {
    if (initialLoadComplete) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedFilters, activeTab, initialLoadComplete, sortConfig, pageSize, viewKey]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // CR-3420: Build ordering parameter from sortConfig
      const ordering = sortConfig.direction === 'asc'
        ? sortConfig.column
        : `-${sortConfig.column}`;

      const queryParams: Record<string, string | number | boolean> = {
        ordering,
        page: currentPage,
        page_size: pageSize,
      };

      // CR-6038: send the active view-context as an explicit scope param so the
      // backend re-scopes immediately, without waiting for the context-persist
      // PATCH to land (which otherwise raced the refetch and returned stale/empty
      // results on a live switch). The ASN scoping honours `site` and `group`;
      // a COMPANY-level selection maps to `site` since company IS site here.
      if (viewLevel === 'GROUP' && selectedGroupId) {
        queryParams['group'] = selectedGroupId;
      } else if (viewLevel === 'COMPANY' && selectedCompanyId) {
        queryParams['site'] = selectedCompanyId;
      } else if (viewLevel === 'SITE' && selectedSiteId) {
        queryParams['site'] = selectedSiteId;
      }

      // CR-6449: Use appliedFilters (not filters) for data fetching
      // Individual field filters using Django filter syntax
      if (appliedFilters.asnNumber) {
        queryParams['asn_number__icontains'] = appliedFilters.asnNumber;
      }

      // Quick box: one term, matched against the part number OR the
      // supplier's own reference on any line of the ASN.
      if (appliedFilters.partSearch) {
        queryParams['part_search'] = appliedFilters.partSearch;
      }

      if (appliedFilters.supplier) {
        queryParams['supplier__name__icontains'] = appliedFilters.supplier;
      }

      if (appliedFilters.trackingNumber) {
        queryParams['tracking_number__icontains'] = appliedFilters.trackingNumber;
      }

      if (appliedFilters.courier) {
        queryParams['courier'] = appliedFilters.courier;
      }

      // Apply status filter from dropdown or active tab
      if (appliedFilters.status) {
        queryParams['status'] = appliedFilters.status;
      } else if (activeTab === 'direct_acknowledged') {
        // CR-7142: not a status — see statusTabs.
        queryParams['direct_acknowledged'] = true;
      } else if (activeTab !== 'all') {
        queryParams['status'] = activeTab;
      }

      // Apply date filters
      if (appliedFilters.dateFrom) {
        queryParams['expected_delivery_date__gte'] = appliedFilters.dateFrom;
      }
      if (appliedFilters.dateTo) {
        queryParams['expected_delivery_date__lte'] = appliedFilters.dateTo;
      }

      // New filter params
      if (appliedFilters.poNumber) {
        queryParams['schedules__po_line__po__po_number__icontains'] = appliedFilters.poNumber;
      }
      if (appliedFilters.poLineNumber) {
        queryParams['schedules__po_line__line_number'] = appliedFilters.poLineNumber;
      }
      if (appliedFilters.site) {
        queryParams['schedules__po_line__po__customer'] = appliedFilters.site;
      }
      if (appliedFilters.supplierGroup) {
        queryParams['supplier__group'] = appliedFilters.supplierGroup;
      }
      if (appliedFilters.commodityGroup) {
        queryParams['schedules__po_line__commodity_group'] = appliedFilters.commodityGroup;
      }
      if (appliedFilters.commodityType) {
        queryParams['schedules__po_line__commodity_type'] = appliedFilters.commodityType;
      }

      if (appliedFilters.courierDeliveryStatus) {
        queryParams['courier_delivery_status'] = appliedFilters.courierDeliveryStatus;
      }

      if (appliedFilters.strategic === 'yes') {
        queryParams['strategic_only'] = 'yes';
      }

      // CR-6449: New filter params for warehouse, supplier country, part number, description
      if (appliedFilters.warehouse) {
        queryParams['warehouse_code'] = appliedFilters.warehouse;
      }
      if (appliedFilters.supplierCountry) {
        queryParams['supplier__country'] = appliedFilters.supplierCountry;
      }
      if (appliedFilters.partNumber) {
        queryParams['schedules__po_line__part_number__icontains'] = appliedFilters.partNumber;
      }
      if (appliedFilters.description) {
        queryParams['schedules__po_line__description__icontains'] = appliedFilters.description;
      }

      // CR-6441: New filter params per diagram
      if (appliedFilters.supplierCode) {
        queryParams['supplier_code'] = appliedFilters.supplierCode;
      }
      if (appliedFilters.buyerId) {
        queryParams['schedules__po_line__buyer_id__icontains'] = appliedFilters.buyerId;
      }

      const response = await purchasingAPI.getASNs(queryParams);

      setAsns(response.data.results);
      setTotalCount(response.data.count);
      // CR-7279: page count from what the server served, not what was asked for.
      const paged = pageCountFromResponse(response.data.count, pageSize, response.data.results.length, currentPage, servedPageRef.current);
      servedPageRef.current = paged.memory;
      setTotalPages(paged.totalPages);

      // CR-7142: refreshed on every load so acknowledging a direct ASN reveals
      // the tab immediately. Asks for a single row — only the count is wanted.
      try {
        const ackRes = await purchasingAPI.getASNs({ direct_acknowledged: true, page_size: 1 });
        setDirectAckCount(ackRes.data.count);
      } catch {
        // A failed count must not blank the list; leave the tab as it was.
      }
    } catch (err: unknown) {
      console.error('Error fetching ASN data:', err);
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Failed to load ASN data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * CR-6449: Apply filters - copies draft `filters` to `appliedFilters`,
   * which triggers the data fetch via useEffect dependency.
   */
  const handleSearch = () => {
    // Sync text inputs to filters before applying
    const filtersWithText = {
      ...filters,
      asnNumber: textInputs.asnNumber,
      supplier: textInputs.supplier,
      trackingNumber: textInputs.trackingNumber,
      poNumber: textInputs.poNumber,
      poLineNumber: textInputs.poLineNumber,
      partNumber: textInputs.partNumber,
      description: textInputs.description,
    };
    setFilters(filtersWithText);
    setAppliedFilters(filtersWithText);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    // CR-6449: Clear both draft and applied filter states
    setTextInputs({
      asnNumber: '',
      supplier: '',
      trackingNumber: '',
      poNumber: '',
      poLineNumber: '',
      partNumber: '',
      description: '',
    });
    setFilters({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
    // The quick box holds its own display state — clear it too, or Clear
    // Filters leaves a term visible that is no longer being applied.
    setPartSearchInput('');
    setQuickSelectValue('');
    if (partSearchTimerRef.current) clearTimeout(partSearchTimerRef.current);
    setCurrentPage(1);
  };

  /**
   * Toolbar quick Part search — its own debounced input, separate from the
   * Filters panel. This IS immediate (updates appliedFilters) since it's
   * outside the filter panel and users expect instant search.
   */
  const [partSearchInput, setPartSearchInput] = useState('');
  const partSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleQuickPartSearch = (value: string) => {
    setPartSearchInput(value);
    setFilters((prev) => ({ ...prev, partSearch: value }));
    if (partSearchTimerRef.current) clearTimeout(partSearchTimerRef.current);
    partSearchTimerRef.current = setTimeout(() => {
      // Update appliedFilters to trigger data fetch
      setAppliedFilters((prev) => ({ ...prev, partSearch: value }));
      setCurrentPage(1);
    }, 400);
  };
  useEffect(() => () => {
    if (partSearchTimerRef.current) clearTimeout(partSearchTimerRef.current);
  }, []);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  /**
   * CR-6449: Filter changes only update the draft `filters` state.
   * CR-6441: Updated to use cascade handlers for dependent filters.
   * The data table does NOT re-render until "Apply Filters" is clicked.
   */
  const handleFilterChange = (key: string, value: string) => {
    // Text inputs: update textInputs state for responsive typing
    if (['asnNumber', 'supplier', 'trackingNumber', 'poNumber', 'poLineNumber', 'partNumber', 'description'].includes(key)) {
      setTextInputs((prev) => ({ ...prev, [key]: value }));
      // Also sync to filters (but NOT appliedFilters - no fetch yet)
      setFilters((prev) => ({ ...prev, [key]: value }));
    } else if (key === 'division') {
      // CR-6441: Cascade: Division → Site → Supplier
      handleDivisionChange(value);
    } else if (key === 'companyCountry') {
      // CR-6441: Cascade: Company Country → Site → Supplier
      handleCompanyCountryChange(value);
    } else if (key === 'site') {
      // CR-6441: Cascade: Site → Supplier
      handleSiteChange(value);
    } else if (key === 'supplierCountry') {
      // CR-6441: Cascade: Supplier Country → Supplier
      // Pass current supplier text from textInputs to avoid stale closure
      handleSupplierCountryChange(value, textInputs.supplier);
    } else if (key === 'supplierGroup') {
      // CR-6441: Cascade: Supplier Group → Supplier
      // Pass current supplier text from textInputs to avoid stale closure
      handleSupplierGroupChange(value, textInputs.supplier);
    } else if (key === 'commodityGroup') {
      // CR-6441: Cascade: Commodity Group → Commodity Type
      handleCommodityGroupChange(value);
    } else {
      // Dropdowns and dates: update filters only (no fetch yet)
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
    // NOTE: Do NOT call setCurrentPage(1) here - page resets on Apply Filters
  };

  /**
   * CR-6449: Handle quick select date range change.
   * Updates draft `filters` only - no immediate fetch.
   */
  const handleQuickSelectChange = (value: string) => {
    setQuickSelectValue(value);
    const today = new Date();
    let dateFrom = '';
    let dateTo = today.toISOString().split('T')[0];

    if (value === '3months') {
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      dateFrom = threeMonthsAgo.toISOString().split('T')[0];
    } else if (value === '12months') {
      const twelveMonthsAgo = new Date(today);
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
      dateFrom = twelveMonthsAgo.toISOString().split('T')[0];
    } else if (value === 'custom') {
      // Custom: just clear dates and let user pick
      dateFrom = '';
      dateTo = '';
    }

    // Update draft filters only - no fetch until Apply Filters
    setFilters((prev) => ({ ...prev, dateFrom, dateTo }));
    // NOTE: Do NOT call setCurrentPage(1) here - page resets on Apply Filters
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  // CR-3420: Handle column sorting
  const handleSort = (column: string) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        // Toggle direction: asc -> desc -> asc
        return {
          column,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      // New column, default to descending
      return { column, direction: 'desc' };
    });
    setCurrentPage(1);
  };

  // CR-3420: Sortable column header component
  const SortableHeader = ({
    column,
    label,
    className = '',
    children,
  }: {
    column: string;
    label: string;
    className?: string;
    children?: React.ReactNode;
  }) => {
    const isActive = sortConfig.column === column;
    return (
      <th
        className={`text-left py-3 px-4 font-medium cursor-pointer hover:bg-muted/50 select-none transition-colors ${className}`}
        onClick={() => handleSort(column)}
      >
        <div className="flex items-center gap-1">
          {children || label}
          <span className="text-muted-foreground">
            {isActive ? (
              sortConfig.direction === 'asc' ? (
                <ArrowUp className="h-3.5 w-3.5" />
              ) : (
                <ArrowDown className="h-3.5 w-3.5" />
              )
            ) : (
              <ArrowUpDown className="h-3.5 w-3.5 opacity-30" />
            )}
          </span>
        </div>
      </th>
    );
  };

  const handleCreateASN = async (data: Record<string, string | number | boolean> | { asn_number: string; schedules: number[]; courier: string; tracking_number: string; method_of_transport: string; weight_kg: string; distance_km: string; expected_delivery_date: string; notes: string }) => {
    try {
      await purchasingAPI.createASN(data as Partial<AdvancedShippingNotice>);
      await fetchData();
    } catch (error: unknown) {
      console.error('Failed to create ASN:', error);
      const axiosErr = error as { response?: { data?: { message?: string } } };
      alert(axiosErr.response?.data?.message || 'Failed to create ASN');
      throw error;
    }
  };

  const handleViewDetails = (asn: AdvancedShippingNotice) => {
    setSelectedAsn(asn);
    setShowDetailModal(true);
  };

  const { formatDate: contextFormatDate, formatDateTime: contextFormatDateTime } = useFormatters();

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return contextFormatDate(dateString);
  };

  // CR-3858: Format date with time for created_at column
  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return contextFormatDateTime(dateString);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; class: string }> = {
      created: { label: 'Created', class: 'bg-gray-100 text-gray-800' },
      submitted: { label: 'Submitted', class: 'bg-blue-100 text-blue-800' },
      // CR-7142: a direct load that has been acknowledged. It is stored as
      // 'submitted' because it must never enter In Transit, so without this the
      // badge would keep saying "Submitted" after the customer acknowledged it.
      acknowledged: { label: 'Acknowledged', class: 'bg-purple-100 text-purple-800' },
      in_transit: { label: 'In Transit', class: 'bg-yellow-100 text-yellow-800' },
      delivered: { label: 'Delivered', class: 'bg-green-100 text-green-800' },
      received: { label: 'Received', class: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-800' },
    };
    const config = statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        {config.label}
      </span>
    );
  };

  // CR-6092: 'Created' is a supplier-only draft state. Customers (and helix
  // admins) must not see unsubmitted drafts, so the Created tab is offered to
  // suppliers only — mirroring the backend get_queryset gate that excludes
  // status='created' from the customer/helix queryset. Without it, switching
  // to a (now-hidden) Created tab would just return an empty list for them.
  const statusTabs: { value: string; label: string }[] = [
    { value: 'all', label: 'All' },
    ...(isSupplier ? [{ value: 'created', label: 'Created' }] : []),
    { value: 'submitted', label: 'Submitted' },
    { value: 'in_transit', label: 'In Transit' },
    // CR-7142: acknowledged direct loads. They are stored as 'submitted'
    // because they must never enter In Transit, so this tab filters on
    // `direct_acknowledged` rather than on a status like its neighbours.
    // Hidden while empty; kept visible once selected, or drilling in would
    // remove the tab under the user and strand the filter.
    ...(directAckCount > 0 || activeTab === 'direct_acknowledged'
      ? [{ value: 'direct_acknowledged', label: 'Acknowledged' }]
      : []),
    { value: 'delivered', label: 'Delivered' },
    { value: 'received', label: 'Received' },
  ];

  /**
   * CR-6449: Count from draft `filters` so badge updates immediately when
   * user selects/enters a value, matching ForecastTab/SO Headers behavior.
   */
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((v) => v !== '').length,
    [filters],
  );

  // CR-6449: Handle export (placeholder - API endpoint not yet implemented)
  const handleExport = () => {
    toast.info('ASN export coming soon');
  };

  if (loading && asns.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">Loading ASNs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchData}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section Tour Trigger */}
      <div className="flex justify-end">
        <SectionTourTrigger
          pageKey="purchasing"
          sectionKey="asn"
          variant="link"
          label="Tour this tab"
        />
      </div>

      {/* CR-6449: Filter Header Bar - matches Dashboard/PO Headers design */}
      <Card>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            {/* Left side: Quick part search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={partSearchInput}
                onChange={(e) => handleQuickPartSearch(e.target.value)}
                placeholder="Search Part or Supplier Part"
                aria-label="Quick search by part number or supplier part number"
                className="pl-8 pr-3 py-2 text-sm border border-border rounded-lg w-72 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            {/* Right side: Action buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchData()}
                className="px-3 py-2 rounded-lg border border-border text-sm flex items-center gap-2 hover:bg-muted transition-colors"
                title="Refresh data"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
              <button
                onClick={handleExport}
                disabled={totalCount === 0}
                className="px-3 py-2 rounded-lg border border-border text-sm flex items-center gap-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Export to Excel"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 transition-colors ${
                  showFilters
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border hover:bg-muted'
                }`}
              >
                <Filter className="h-4 w-4" />
                Filters
                {activeFilterCount > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    showFilters
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-primary text-primary-foreground'
                  }`}>
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {/* CR-6038: Import Delivery Lines (standalone) */}
              {isSupplier && (
                <button
                  onClick={() => setShowImportAllocate(true)}
                  className="px-3 py-2 text-sm border border-blue-600 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-1"
                  data-tour="import-asn-lines-btn"
                >
                  <FileUp className="h-4 w-4" />
                  Import Lines
                </button>
              )}
              {/* Create ASN button - suppliers only */}
              {isSupplier && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
                  data-tour="create-asn-btn"
                >
                  <Plus className="h-4 w-4" />
                  Create ASN
                </button>
              )}
            </div>
          </div>
        </CardHeader>

        {/* Filter Sections - only visible when filters are shown */}
        {showFilters && (
          <CardContent className="pt-0 pb-4">
            <ASNFilterSection
              filters={filters}
              textInputs={textInputs}
              siteOptions={effectiveSiteOptions}
              warehouseOptions={warehouseOptions}
              supplierCountryOptions={supplierCountryOptions}
              supplierGroupOptions={supplierGroupOptions}
              commodityGroupOptions={commodityGroupOptions}
              commodityTypeOptions={effectiveCommodityTypeOptions}
              quickSelectValue={quickSelectValue}
              onFilterChange={handleFilterChange}
              onQuickSelectChange={handleQuickSelectChange}
              onClearFilters={clearFilters}
              onSearch={handleSearch}
              onHideFilters={() => setShowFilters(false)}
              isSupplier={isSupplier}
              divisionOptions={divisionOptions}
              siteCountries={siteCountries}
              trackingEnabled={trackingEnabled}
            />
          </CardContent>
        )}
      </Card>

      {/* Header with Tabs */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold">Advanced Shipping Notices</h3>
              <span className="text-sm text-muted-foreground">
                {totalCount} {totalCount === 1 ? 'ASN' : 'ASNs'}
              </span>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList
              className={`grid w-full ${
                statusTabs.length === 7
                  ? 'grid-cols-7'
                  : statusTabs.length === 6
                    ? 'grid-cols-6'
                    : 'grid-cols-5'
              }`}
              data-tour="asn-status-tabs"
            >
              {statusTabs.map((t) => (
                <TabsTrigger key={t.value} value={t.value}>{t.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* ASN Table */}
      <Card data-tour="asn-table">
        <CardContent className="p-0">
          {asns.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No ASNs found matching your criteria.
            </div>
          ) : (
            <>
              {/* Top Pagination */}
              <div className="px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize} servedPerPage={servedPageRef.current?.served}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemLabel="ASNs"
                />
              </div>

              {/* CR-3866: Added max-height and sticky header support */}
              <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-350px)]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-20">
                    <tr className="border-b bg-muted/80 backdrop-blur-sm">
                      <th className="text-left py-3 px-4 font-medium sticky left-0 bg-muted/30 z-10 min-w-[100px]">View</th>
                      <SortableHeader column="asn_number" label="ASN Number" className="min-w-[140px]" />
                      <SortableHeader column="status" label="Status" className="min-w-[140px]" />
                      <SortableHeader column="supplier__name" label="Supplier" className="min-w-[180px]" />
                      <SortableHeader column="customer__name" label="Customer" className="min-w-[180px]" />
                      <th className="text-left py-3 px-4 font-medium min-w-[180px]">Company</th>
                      <SortableHeader column="courier" label="Courier" className="min-w-[120px]" />
                      <SortableHeader column="tracking_number" label="Tracking Number" className="min-w-[160px]" />
                      {/* CR-3415: Premium Feature - Courier Status (not sortable) */}
                      <th className="text-left py-3 px-4 font-medium min-w-[160px]">
                        <div className="flex items-center gap-1">
                          Courier Status
                          <span className="px-1.5 py-0.5 text-[10px] bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full font-bold flex items-center gap-0.5" title="Premium Feature - Real-time tracking via Courier API">
                            <Sparkles className="h-2.5 w-2.5" />
                            PRO
                          </span>
                        </div>
                      </th>
                      <SortableHeader column="method_of_transport" label="Transport Method" className="min-w-[140px]" />
                      <SortableHeader column="weight_kg" label="Weight (kg)" className="min-w-[110px] text-right" />
                      <SortableHeader column="distance_km" label="Distance (km)" className="min-w-[120px] text-right" />
                      <SortableHeader column="carbon_emission_kg" label="Carbon (kg CO₂)" className="min-w-[130px] text-right" />
                      {/* CR-3415: Premium Feature - Expected Delivery with live updates */}
                      <SortableHeader column="expected_delivery_date" label="Expected Delivery" className="min-w-[160px]">
                        <span>Expected Delivery</span>
                        <span className="px-1.5 py-0.5 text-[10px] bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full font-bold flex items-center gap-0.5 ml-1" title="Premium Feature - Auto-updated via Courier API">
                          <Sparkles className="h-2.5 w-2.5" />
                          PRO
                        </span>
                      </SortableHeader>
                      <SortableHeader column="actual_delivery_date" label="Actual Delivery" className="min-w-[140px]" />
                      {/* CR-3858: Created date column */}
                      <SortableHeader column="created_at" label="Created" className="min-w-[140px]" />
                    </tr>
                  </thead>
                  <tbody>
                    {asns.map((asn: AdvancedShippingNotice) => (
                      <tr
                        key={asn.id}
                        className="border-b hover:bg-muted/50 transition-colors"
                      >
                        <td className="py-3 px-4 sticky left-0 bg-background z-10">
                          <button
                            onClick={() => handleViewDetails(asn)}
                            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center gap-1"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </button>
                        </td>
                        <td className="py-3 px-4 font-medium">{asn.asn_number}</td>
                        <td className="py-3 px-4">{getStatusBadge(asnDisplayStatus(asn))}</td>
                        <td className="py-3 px-4">{asn.supplier_details?.name || '-'}</td>
                        <td className="py-3 px-4">{asn.customer_name || asn.customer_details?.name || '-'}</td>
                        <td className="py-3 px-4">{asn.customer_details?.name || '-'}</td>
                        <td className="py-3 px-4 uppercase">{asn.courier || '-'}</td>
                        <td className="py-3 px-4 font-mono">{asn.tracking_number || '-'}</td>
                        {/* CR-3415: Courier Status - Premium Feature */}
                        <td className="py-3 px-4">
                          {asn.tracking_number && asn.courier ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-500 border border-dashed border-gray-300">
                              <Sparkles className="h-3 w-3 text-amber-500" />
                              Enable PRO
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 capitalize">{asn.method_of_transport || '-'}</td>
                        <td className="py-3 px-4 text-right">{asn.computed_weight_kg ?? asn.weight_kg ?? '-'}</td>
                        <td className="py-3 px-4 text-right">{asn.distance_km || '-'}</td>
                        <td className="py-3 px-4 text-right font-medium text-green-700">
                          {asn.carbon_emission_kg || asn.calculated_carbon_emission || '-'}
                        </td>
                        <td className="py-3 px-4">{formatDate(asn.estimated_delivery_date || '')}</td>
                        <td className="py-3 px-4">{formatDate(asn.actual_delivery_date || '')}</td>
                        {/* CR-3858: Created date and time cell */}
                        <td className="py-3 px-4">{formatDateTime(asn.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Bottom Pagination */}
              <div className="px-4">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                  itemsPerPage={pageSize} servedPerPage={servedPageRef.current?.served}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  itemLabel="ASNs"
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center justify-center p-4">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      {/* Create ASN Modal */}
      <CreateASNModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateInitialCallOffId(null);
          setCreatePrefilledDeliveryLineIds([]);
        }}
        onSubmit={handleCreateASN}
        initialCallOffId={createInitialCallOffId ?? undefined}
        initialDeliveryLineIds={createPrefilledDeliveryLineIds.length > 0 ? createPrefilledDeliveryLineIds : undefined}
      />

      {/* CR-6038: Standalone Import & Allocate workflow */}
      <ImportAllocateModal
        isOpen={showImportAllocate}
        onClose={() => setShowImportAllocate(false)}
        onCreateNewASN={(deliveryLineIds) => {
          setCreatePrefilledDeliveryLineIds(deliveryLineIds);
          setShowCreateModal(true);
        }}
        onAttached={() => fetchData()}
      />

      {/* ASN Detail Modal */}
      {showDetailModal && selectedAsn && (
        <ASNDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedAsn(null);
          }}
          asn={selectedAsn}
          onSubmit={async () => await fetchData()}
        />
      )}
    </div>
  );
}
